import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DOMPurify from 'dompurify';
import useSeo from '../hooks/useSeo';
import { usePageContent } from './home/pageContent';
import { Accordion, PageHero, PageShell, SectionIntro } from './home/blocks';
import CtaBanner from './home/CtaBanner';

const EMPTY_LABELS = {
  nl: 'Nog geen vragen beschikbaar.', fr: 'Aucune question disponible.', de: 'Noch keine Fragen verfügbar.',
  es: 'Aún no hay preguntas disponibles.', en: 'No questions available yet.', cn: '暂无可用问题。',
};

/* The FAQ backend uses "cn" for Chinese. */
const uiToFaqLang = (uiLang) => {
  const short = (uiLang || 'nl').slice(0, 2);
  if (short === 'zh') return 'cn';
  return ['nl', 'fr', 'de', 'es', 'en'].includes(short) ? short : 'nl';
};

/*
 * Questions: Admin → FAQ. Page texts (hero, banner): Admin → Landingspagina → FAQ.
 */
const FaqPage = () => {
  const { t, i18n } = useTranslation();
  const { lang, cms, content } = usePageContent('faq');
  const faqLang = uiToFaqLang(i18n.language);
  const [data, setData] = useState({ categories: [] });
  const [loading, setLoading] = useState(true);

  useSeo({
    path: '/faq',
    lang,
    title: cms?.seo?.title || t('faq_ui.seo_title'),
    description: cms?.seo?.description || t('faq_ui.seo_description'),
  });

  useEffect(() => {
    setLoading(true);
    axios
      .get(`/faq?lang=${faqLang}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ categories: [] }))
      .finally(() => setLoading(false));
  }, [faqLang]);

  // schema.org FAQPage for rich results
  useEffect(() => {
    const all = (data.categories || []).flatMap((c) => c.items || []);
    if (all.length === 0) return undefined;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: all.map((it) => ({
        '@type': 'Question',
        name: it.question,
        acceptedAnswer: { '@type': 'Answer', text: (it.answer || '').replace(/<[^>]+>/g, ' ').trim() },
      })),
    });
    document.getElementById('faq-schema')?.remove();
    document.head.appendChild(script);
    return () => document.getElementById('faq-schema')?.remove();
  }, [data]);

  const status = (text) => <p className="py-12 text-center text-[clamp(16px,1.1111vw,20px)] text-brand-muted">{text}</p>;

  return (
    <PageShell testId="faq-page">
      <PageHero hero={content.hero} />
      <SectionIntro />

      <section className="hp-container mt-[clamp(28px,4vw,72px)]">
        <div className="mx-auto max-w-[1200px]">
          {loading
            ? status(content.labels.loading)
            : data.enabled === false
              ? status(content.labels.disabled)
              : (data.categories || []).length === 0
                ? status(EMPTY_LABELS[faqLang] || EMPTY_LABELS.nl)
                : data.categories.map((cat, ci) => (
                  <div key={cat.id} className={ci > 0 ? 'mt-[clamp(40px,4.4444vw,80px)]' : ''} data-testid={`faq-category-${cat.id}`}>
                    <h2 className="mb-[clamp(16px,1.6667vw,30px)] text-[clamp(24px,2.2222vw,40px)] font-bold leading-tight text-[#273845]">
                      {cat.name}
                    </h2>
                    <Accordion
                      items={cat.items}
                      renderAnswer={(item) => (
                        <div className="faq-answer" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.answer) }} />
                      )}
                    />
                  </div>
                ))}
        </div>
      </section>

      <div className="pt-[clamp(0px,5.3333vw,96px)]">
        <CtaBanner cta={content.cta} wide />
      </div>
    </PageShell>
  );
};

export const FaqFooterLink = ({ className }) => {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    axios.get('/faq/settings')
      .then((r) => setShow(r.data?.enabled && r.data?.show_in_footer))
      .catch(() => setShow(false));
  }, []);
  if (!show) return null;
  return <li><a href="/faq" className={className} data-testid="footer-faq-link">FAQ</a></li>;
};

export default FaqPage;
